/**
 * Shared vocabulary for the tokenization lifecycle screens.
 *
 * Labels and formatters only. Every FACT on these screens comes off the wire: the stage tracker is
 * `application.stages`, the checklist is `application.checklist`, and the three approvals are
 * `offer.approvals`. Nothing here decides anything, and that is deliberate - a UI that computed which
 * stage an application was in would be a second state machine, disagreeing with the first one on the
 * day somebody adds a status.
 */

import type { TokenizationStatus } from './api';

/**
 * What a document type is CALLED. The backend sends `title_deed`; a person reading their own
 * checklist should see "Title deed".
 *
 * A missing entry falls back to the raw key rather than to a guess, so a document type added to a
 * product shows up as `rates_clearance` and gets noticed, instead of rendering as nothing.
 */
export const DOC_LABELS: Record<string, string> = {
  owner_identification: 'Identity document',
  title_deed: 'Title deed',
  search_certificate: 'Official search',
  survey_plan: 'Survey plan',
  property_photograph: 'Photographs',
  rates_clearance: 'Rates clearance',
  tax_compliance: 'Tax compliance certificate',
  insurance_policy: 'Insurance certificate',
  lease_agreement: 'Tenancy agreements',
  valuation_report: 'Valuation report',
  vehicle_logbook: 'Logbook',
  inspection_report: 'Inspection report',
  invoice: 'Invoice or contract',
  other_supporting: 'Supporting documents',
  affidavit_of_ownership: 'Affidavit of ownership',
  encumbrance_certificate: 'Encumbrance certificate',
  certificate_of_formation: 'Certificate of formation',
  operating_agreement: 'Operating agreement',
  series_designation: 'Series designation',
  transfer_to_entity: 'Transfer to the holding entity',
};

export function docLabel(docType: string): string {
  return DOC_LABELS[docType] ?? docType;
}

/**
 * The one-line answer to "what is happening to my asset right now".
 *
 * Written in the second person and about the NEXT actor, because "under_trust_review" tells an owner
 * nothing and "the trust is reviewing it" tells them who to wait for. The statuses that need the
 * owner to act say so first.
 */
export const STATUS_SUMMARY: Record<TokenizationStatus, string> = {
  draft: 'Not submitted yet. Finish it when you are ready.',
  submitted: 'With the platform. A reviewer will pick it up.',
  under_vasp_review: 'A compliance officer is reviewing your submission.',
  information_requested: 'We need something more from you before this can go on.',
  under_trust_review: 'With the trust, which decides whether it will hold this asset.',
  approved_for_valuation: 'Approved for valuation. A valuer has been assigned.',
  valuation_in_progress: 'The valuer is assessing your asset.',
  valuation_submitted: 'The valuation is in and is being checked by the trust.',
  under_trust_reconciliation: 'The trust is reconciling the valuation against the asset.',
  tokenization_offer_pending: 'The valuation is approved. Your offer is being prepared.',
  offer_pending_user_approval: 'Your offer is ready. Nothing is issued until you agree to it.',
  offer_pending_vasp_approval: 'You have agreed. The platform is making the final approval.',
  tokenization_authorized: 'Approved by all three parties. Your tokens are being issued.',
  tokens_issued: 'Your tokens are issued and in your portfolio.',
  listed_on_secondary_market: 'Part of your holding is listed on the marketplace.',
  completed: 'Complete.',
  rejected: 'This application was not taken forward.',
  withdrawn: 'You withdrew this application.',
};

/** Maps a lifecycle status onto the shared StatusPill vocabulary. */
export function statusTone(status: TokenizationStatus): string {
  if (status === 'rejected' || status === 'withdrawn') return 'failed';
  if (
    status === 'tokens_issued' ||
    status === 'completed' ||
    status === 'listed_on_secondary_market'
  )
    return 'verified';
  if (status === 'offer_pending_user_approval' || status === 'information_requested')
    return 'action_required';
  if (status === 'draft') return 'draft';
  return 'in review';
}

/** WHO took an action, in words. `system` is the platform acting on an approval two people signed. */
export const ACTOR_LABEL: Record<string, string> = {
  applicant: 'You',
  vasp: 'VASP',
  trust: 'The trust',
  valuer: 'The valuer',
  system: 'The platform',
};

/**
 * A minor-unit amount as a readable figure. KES has two places.
 *
 * Through BigInt and never through Number: a valuation is up to eighteen digits of minor units and
 * `Number.MAX_SAFE_INTEGER` is sixteen, so parsing one as a float loses shillings on a large property
 * and does it silently.
 */
export function formatMinor(minor: string | null | undefined, asset = 'KES'): string {
  if (minor === null || minor === undefined || minor === '') return '-';

  const places = asset === 'KES' ? 2 : 2;
  const negative = minor.startsWith('-');
  const digits = (negative ? minor.slice(1) : minor).replace(/\D/g, '') || '0';
  const padded = digits.padStart(places + 1, '0');

  const whole = padded.slice(0, padded.length - places);
  const fraction = padded.slice(padded.length - places);
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${negative ? '-' : ''}${grouped}.${fraction}`;
}

/**
 * A token quantity as a readable figure.
 *
 * The wire carries eight decimal places because a token is fractionalizable to eight. Showing
 * `2000.00000000` on a card is noise, so trailing zeros go; showing `2000` when the holding is
 * actually `2000.5` would be a lie, so a real fraction stays.
 */
export function formatUnits(units: string | null | undefined): string {
  if (!units) return '0';
  const [whole = '0', fraction = ''] = units.split('.');
  const trimmed = fraction.replace(/0+$/, '');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return trimmed ? `${grouped}.${trimmed}` : grouped;
}

/** Compare two token quantities without going through a float. */
export function unitsExceed(a: string, b: string): boolean {
  return toMinorUnits(a) > toMinorUnits(b);
}

/** Exact minor units back to a whole-token string, ready for `formatUnits`. Never through Number. */
export function unitsFromMinor(minor: bigint): string {
  const whole = minor / 100_000_000n;
  const fraction = minor % 100_000_000n;
  return `${whole}.${fraction.toString().padStart(8, '0')}`;
}

/** Whole tokens as exact minor units (1e8 per token). Never through Number. */
export function toMinorUnits(whole: string): bigint {
  const clean = String(whole).trim().replace(/,/g, '');
  if (!clean || !/^\d*(\.\d*)?$/.test(clean)) return 0n;
  const [head = '0', tail = ''] = clean.split('.');
  return BigInt(head || '0') * 100_000_000n + BigInt((tail + '00000000').slice(0, 8));
}
